import { gql } from "@apollo/client";
import { UpdateMembershipDocument, MembershipLevelComponentDocument } from "../types";
import { decodeSanitizedText } from "../helpers/tools";
import { unsavedMembershipTierEditsVar } from "../helpers/client";
export const writeMembershipTier = (tierId, newTier, client) => {
    client.cache.modify({
        id: `MembershipTier:${tierId}`,
        fields: {
            discounts: () => {
                return [{
                        __typename: 'Discount',
                        scope: 'RETAIL',
                        percentage: newTier.discount,
                    }];
            },
            plans: () => {
                return [{
                        __typename: 'Plan',
                        kind: 'MONTHLY',
                        price: {
                            __typename: 'Price',
                            amount: newTier.price
                        },
                    }, {
                        __typename: 'Plan',
                        kind: 'ANNUAL',
                        price: {
                            __typename: 'Price',
                            amount: newTier.price * 12
                        },
                    }];
            },
        },
    });
};
export const saveMemberships = async (businessId, client, options, tierIds) => {
    const tiers = client.readFragment({
        id: `Business:${businessId}`,
        fragment: gql `
      fragment SaveMemberships on Business {
        membershipTiers(kind:Live) {
          id
          name
          defaultMode
          overridePlatformProductSync
          plans {
            kind
            price {
              amount
            }
          }
          discounts {
            scope
            percentage
            guests
          }
          earlyAccess
          birthdayCouponAmount
          birthdayCouponKind
          welcomeGift {
            __typename
            id
            name
            shippingNotesInstructions
            products {
              id
              name
              platformId
              platformName
              platformVariantsOnlyMode
              variants {
                id
                description
                additionalCost {
                  amount
                }
                platformName
                platformId
              }
            }
            variants {
              id
              description
              additionalCost {
                amount
              }
              platformName
              platformId
            }
          }
          benefits {
            __typename
            id
            name
            ...on RegularBenefit {
              order
            }
            ...on RedemptionBenefit {
              delivery
              perPeriod
              shippingNotesInstructions
            }
            ...on SubscriptionBenefit {
              delivery
              nextShipmentDate
              isAddonOnly
              shippingFrequency {
                number
                period
              }
              deliveryFrequency
              firstShipmentDelay
              shippingIncluded
              shippingWeight
              shippingNotesInstructions
              curationMatchMode
              curationPreferOverstock
              products {
                id
                name
                platformId
                platformName
                platformVariantsOnlyMode
                curationValue
                curationCollections
                curationExcludeProducts
                variants {
                  id
                  description
                  additionalCost {
                    amount
                  }
                  platformName
                  platformId
                  curationExcludeProducts
                  curationIncludeVariants
                  curationSearchTerms
                  curationStatus
                }
              }
              variants {
                id
                description
                additionalCost {
                  amount
                }
                platformName
                platformId
              }
              addons {
                id
                description
                applyDiscount
                dontRollOver
                platformId
                platformName
                additionalCost {
                  amount
                }
              }
            }
          }
        }
      }
    `,
    });
    if (!tiers) {
        console.error("Tiers not saved; could not fetch from cache");
        if (process.env.NODE_ENV === "development") {
            throw new Error("Could not save membership tiers");
        }
        else
            return;
    }
    const tiersToSave = tierIds ? tiers.membershipTiers.filter(tier => tierIds.includes(tier.id)) : tiers.membershipTiers;
    const overlayMap = unsavedMembershipTierEditsVar();
    await Promise.all(tiersToSave.map((tier) => {
        // Merge unsaved overlay values (if any) so we persist what user currently sees
        const overlay = (overlayMap === null || overlayMap === void 0 ? void 0 : overlayMap[tier.id]) || {};
        // Shallow merge for simple fields; for arrays we prefer overlay arrays when provided
        const effectiveTier = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, tier), (overlay.name !== undefined ? { name: overlay.name } : {})), (overlay.earlyAccess !== undefined ? { earlyAccess: overlay.earlyAccess } : {})), (overlay.plans !== undefined ? { plans: overlay.plans } : {})), (overlay.discounts !== undefined ? { discounts: overlay.discounts } : {})), (overlay.benefits !== undefined ? { benefits: overlay.benefits } : {}));
        const membershipInput = Object.assign(Object.assign({}, effectiveTier), { name: decodeSanitizedText(tier.name) || 'New Membership', welcomeGift: tier.welcomeGift ? Object.assign(Object.assign({}, tier.welcomeGift), { name: decodeSanitizedText(tier.welcomeGift.name) || 'Welcome Gift' }) : null, 
            // Exclude WelcomeGiftBenefit from benefits; it should only be set via welcomeGift field
            benefits: (effectiveTier.benefits || tier.benefits || [])
                .filter((b) => (b === null || b === void 0 ? void 0 : b.__typename) !== 'WelcomeGiftBenefit')
                .map((b) => {
                const decodedName = decodeSanitizedText(b.name);
                const newBenefit = Object.assign(Object.assign({}, b), { name: decodedName });
                if (newBenefit.__typename === 'SubscriptionBenefit') {
                    return {
                        subscription: Object.assign(Object.assign({}, newBenefit), { name: decodedName || 'Curated item from our collection' }),
                    };
                }
                if (newBenefit.__typename === 'RedemptionBenefit') {
                    return {
                        redemption: {
                            id: newBenefit.id,
                            delivery: newBenefit.delivery,
                            perPeriod: newBenefit.perPeriod,
                            shippingNotesInstructions: newBenefit.shippingNotesInstructions,
                            name: decodedName || newBenefit.name,
                        }
                    };
                }
                return { regular: newBenefit };
            }) });
        if (options && Object.prototype.hasOwnProperty.call(options, 'makeNonDefault')) {
            // @ts-ignore
            membershipInput.defaultMode = !options.makeNonDefault;
        }
        return client.mutate({
            mutation: UpdateMembershipDocument,
            variables: {
                id: businessId,
                membershipInput
            },
            refetchQueries: [MembershipLevelComponentDocument],
            update: (cache, result) => {
                var _a;
                const updateOrCreateMembership = (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.updateOrCreateMembership;
                // Update tier name in cache (best-effort)
                cache.modify({
                    id: `MembershipTier:${updateOrCreateMembership.id}`,
                    fields: {
                        name: () => updateOrCreateMembership.name,
                    }
                });
            }
        });
    }));
};
export const processUpload = (currentFile) => {
    if (!(currentFile === null || currentFile === void 0 ? void 0 : currentFile.originalImageInfo))
        return;
    const params = {
        name: currentFile === null || currentFile === void 0 ? void 0 : currentFile.name,
        mimeType: currentFile === null || currentFile === void 0 ? void 0 : currentFile.mimeType,
        path: currentFile === null || currentFile === void 0 ? void 0 : currentFile.cdnUrl,
    };
    return params;
};
